import axios from 'axios';
import { useCallback, useContext, useState } from 'react';
import { useLoaderData } from 'react-router-dom';

import { InterestedOptions } from '@revelio/core';

import { environment } from '../../environment';
import { SurfContext } from '../../surf-context';
import { AboutYouFields } from '../components/about-you-fields';
import { ThemeHeading, ThemeTagline } from '../components/theme/theme';
import { ThemeLayout } from '../components/theme/theme-layout';
import { Stack, uiStyles } from '../components/ui';
import { personalizedReturnUrl } from '../utils/personalized-return-url';

/**
 * Returning-user personalization: the login flow (and the dashboard's
 * AuthGate) routes live-trial users here instead of the dashboard while
 * their `personalization_completed` metadata is empty — i.e. they bailed out
 * of signup before the "tell us about yourself" step. Submitting stamps the
 * metadata via /interested-trial, so the gate opens on the next visit. The
 * route loader guarantees an active session (email + userId) or redirects
 * to /login.
 */
function Personalize() {
  const csrfToken = useContext(SurfContext);
  const session = useLoaderData() as { email: string; userId: string };

  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState('');
  const [interest, setInterest] = useState('');
  // The academic path has no self-serve dashboard — mirror signup's step 3.
  const [academicSubmitted, setAcademicSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setSubmitError('');
    try {
      await axios.post(
        `${environment.PUBLIC_USER_MGMT_URL}/api/users/interested-trial`,
        {
          email: session.email,
          interests: [interest],
          company: organization.trim(),
          userId: session.userId,
        },
        {
          timeout: 0,
          headers: {
            'x-surf-token': csrfToken,
          },
          withCredentials: true,
          xsrfCookieName: 'x-surf-token',
          xsrfHeaderName: 'x-surf-token',
        }
      );
    } catch (error) {
      // Leaving for the dashboard is only safe once the request confirms the
      // gate is open — AuthGate sends an unstamped user straight back here,
      // and each round trip re-sends the CRM lead. Stay put and let them
      // retry instead.
      console.error(error);
      setSubmitError('Something went wrong, please try again.');
      setLoading(false);
      return;
    }
    setLoading(false);
    if (interest === InterestedOptions.Academic_Research) {
      setAcademicSubmitted(true);
    } else {
      window.location.replace(
        personalizedReturnUrl(environment.PUBLIC_REACT_APP_URL)
      );
    }
  }, [csrfToken, interest, organization, session]);

  return (
    <ThemeLayout variant="centered">
      <Stack gap="var(--ap-code-gap)" fullWidth>
        {academicSubmitted ? (
          <Stack gap="12px">
            <ThemeHeading>Thanks — we'll be in touch</ThemeHeading>
            <ThemeTagline>
              Our economics team will reach out about academic access shortly.
            </ThemeTagline>
          </Stack>
        ) : (
          <>
            <Stack gap="12px">
              <ThemeHeading>Tell us a little about yourself</ThemeHeading>
              <ThemeTagline>
                Help us personalize your experience and recommend relevant
                workforce insights.
              </ThemeTagline>
            </Stack>
            <AboutYouFields
              organization={organization}
              onOrganizationChange={setOrganization}
              interest={interest}
              onInterestChange={setInterest}
              onContinue={handleSubmit}
              loading={loading}
            />
            {submitError && <p className={uiStyles.formError}>{submitError}</p>}
          </>
        )}
      </Stack>
    </ThemeLayout>
  );
}

export default Personalize;
